import React, { useState, useEffect, useRef } from 'react';
import { IoClose } from 'react-icons/io5';
import QuickPicks from './QuickPicks';
import MiniCalendar from './MiniCalendar';
import TimeInput from './TimeInput';

const SchedulePopover = ({ isOpen, onClose, onSchedule, message }) => {
    const [selectedDate, setSelectedDate] = useState(() => {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        tomorrow.setHours(9, 0, 0, 0);
        return tomorrow;
    });
    const [hour, setHour] = useState(9);
    const [minute, setMinute] = useState(0);
    const popoverRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (popoverRef.current && !popoverRef.current.contains(event.target)) {
                onClose();
            }
        };

        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('keydown', handleEscape);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const handleQuickPick = (date) => {
        onSchedule(date);
    };

    const handleDateChange = (date) => {
        const newDate = new Date(date);
        newDate.setHours(hour, minute, 0, 0);
        setSelectedDate(newDate);
    };

    const handleHourChange = (newHour) => {
        setHour(newHour);
        const newDate = new Date(selectedDate);
        newDate.setHours(newHour, minute, 0, 0);
        setSelectedDate(newDate);
    };

    const handleMinuteChange = (newMinute) => {
        setMinute(newMinute);
        const newDate = new Date(selectedDate);
        newDate.setHours(hour, newMinute, 0, 0);
        setSelectedDate(newDate);
    };

    const handleScheduleClick = () => {
        const finalDate = new Date(selectedDate);
        finalDate.setHours(hour, minute, 0, 0);
        onSchedule(finalDate);
    };

    const formatScheduleDate = (date) => {
        const now = new Date();
        const tomorrow = new Date(now);
        tomorrow.setDate(tomorrow.getDate() + 1);

        const isSameDay = (d1, d2) => {
            return d1.getFullYear() === d2.getFullYear() &&
                d1.getMonth() === d2.getMonth() &&
                d1.getDate() === d2.getDate();
        };

        const timeStr = date.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });

        if (isSameDay(date, now)) {
            return `Today ${timeStr}`;
        } else if (isSameDay(date, tomorrow)) {
            return `Tomorrow ${timeStr}`;
        } else {
            return date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
            });
        }
    };

    return (
        <div
            ref={popoverRef}
            className="fixed bottom-20 right-6 w-80 bg-white/90 backdrop-blur-2xl border border-white/40 shadow-2xl rounded-2xl p-4 z-50 animate-scaleIn"
            style={{
                transformOrigin: 'bottom right',
            }}
        >
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-900">Schedule Message</h3>
                <button
                    onClick={onClose}
                    className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                >
                    <IoClose className="w-5 h-5 text-gray-500" />
                </button>
            </div>

            {/* Quick Picks */}
            <QuickPicks onSelect={handleQuickPick} />

            <div className="my-4 border-t border-gray-200" />

            {/* Custom Picker */}
            <div className="space-y-4">
                <MiniCalendar
                    selectedDate={selectedDate}
                    onSelectDate={handleDateChange}
                />

                <TimeInput
                    hour={hour}
                    minute={minute}
                    onHourChange={handleHourChange}
                    onMinuteChange={handleMinuteChange}
                />
            </div>

            {/* Schedule Button */}
            <button
                onClick={handleScheduleClick}
                disabled={(() => {
                    const finalDate = new Date(selectedDate);
                    finalDate.setHours(hour, minute, 0, 0);
                    const now = new Date();
                    const isPast = finalDate.getTime() <= now.getTime();

                    return isPast;
                })()}
                className={`w-full rounded-xl py-3 mt-4 shadow-lg font-medium transition-colors ${(() => {
                    const finalDate = new Date(selectedDate);
                    finalDate.setHours(hour, minute, 0, 0);
                    return finalDate.getTime() <= new Date().getTime();
                })()
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-black text-white hover:bg-gray-900'
                    }`}
            >
                Schedule Message
            </button>

            <style jsx>{`
        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .animate-scaleIn {
          animation: scaleIn 200ms ease-out;
        }

        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }

        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 2px;
        }

        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #888;
          border-radius: 2px;
        }

        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #555;
        }
      `}</style>
        </div>
    );
};

export default SchedulePopover;
